import React, { useContext, useEffect, useState } from 'react'

import { Form, Typography } from '@s4l/ds'
import { useS4LContext } from '@s4l/react-context'
import { CriteriaAvailability } from '@s4l/stayforlong/booking/CriteriaAvailability'
import type { Availability } from '@s4l/stayforlong/booking/Availability'
import { useUseCase } from '@s4l/utils/hooks/useUseCase'

import { RoomConfiguratorContext } from '../context'
import RadioSection from './RadioSection'
import Footer from './Footer'
import Info from './Info'
import RadioItem from './RadioItem'
import Placeholder from '../Placeholder'

import styles from './styles.module.css'
import type { PaymentPolicies } from '@s4l/stayforlong/booking/PaymentPolicy'
import type { Currencies } from '@s4l/stayforlong/kernel/Currency'
import { PriceType } from '@s4l/stayforlong/kernel/PriceType'
import type { UtilityClassNames } from '@s4l/utility-css'
import { ErrorsCode } from '@s4l/stayforlong/kernel/ErrorsCode'
import { DomainError } from '@s4l/stayforlong/kernel/DomainError'

interface IRoomConfiguratorProps {
  loading?: boolean
  onSelectAvailability: (availability: Availability) => void
  onChange?: ({
    kind,
    availability,
  }: {
    kind: 'rate' | 'board'
    availability: Availability
  }) => void
  onAvailabilitySoldOut?: () => void
}

function RoomConfiguratorForm({
  onSelectAvailability,
  onChange,
  loading = false,
  onAvailabilitySoldOut,
}: Readonly<IRoomConfiguratorProps>) {
  const { listOfAvailability: listOfAvailabilityInit, roomID } = useContext(RoomConfiguratorContext)
  const [listOfAvailability, setListOfAvailability] = useState(listOfAvailabilityInit)
  const [loadingBookingRule, setLoadingBookingRule] = useState(false)
  const [availabilityMatch, setAvailabilityMatch] = useState(() =>
    listOfAvailability.getCheapestAvailability()
  )

  const {
    data: market,
    loading: loadingMarket,
    error: errorMarket,
  } = useUseCase('GetCurrentMarketSharedUseCase')

  const {
    data: affiliate,
    loading: isLoadingAffiliate,
    error: errorAffiliate,
  } = useUseCase('GetCurrentAffiliateSharedUseCase')

  const listOfBoards = listOfAvailability.getListOfBoardByRoomID({ roomID }).unique()
  const listOfRates = listOfAvailability.getListOfRatesByRoomID({ roomID }).unique()

  const { i18n, domain } = useS4LContext()

  useEffect(() => {
    if (!availabilityMatch?.isPurchasable()) {
      onAvailabilitySoldOut?.()
    }

    if (
      !availabilityMatch ||
      !availabilityMatch?.isRecheck() ||
      loadingBookingRule ||
      !availabilityMatch.isPurchasable()
    )
      return

    setLoadingBookingRule(true)
    domain.GetBookingRuleBookingUseCase.execute({
      availabilityJSON: availabilityMatch.toJSON(),
    })
      .then(([error, availability]) => {
        if (
          error instanceof DomainError &&
          error.hasCode(ErrorsCode.AVAILABILITY_NOT_PURCHASABLE)
        ) {
          const availabilityToUpdate = availabilityMatch.markAsNoPurchasable()
          setListOfAvailability(
            listOfAvailability.updateAvailabilityById({ availability: availabilityToUpdate })
          )
          setAvailabilityMatch(availabilityToUpdate)
          return
        }

        if (error) return // the error is other than 410

        setListOfAvailability(listOfAvailability.updateAvailabilityById({ availability }))
        setAvailabilityMatch(availability)
      })
      .finally(() => {
        setLoadingBookingRule(false)
      })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [availabilityMatch])

  const handleChange = ({ rate, board }: { rate: string; board: string }) => {
    const [paymentPolicy, cancellationPolicyFromDate, cancellationPolicyAmount] = rate.split('_') as [PaymentPolicies, string, string] // prettier-ignore

    const matchingRate = listOfRates.find(r => {
      const cancellationPolicyAmountValue =
        cancellationPolicyAmount !== undefined ? Number(cancellationPolicyAmount) : 0

      return (
        r.paymentPolicy() === paymentPolicy &&
        r.cancellationPolicyFromDate() === cancellationPolicyFromDate &&
        (r.cancellationPolicyAmount() ?? 0) === cancellationPolicyAmountValue
      )
    })

    const matchingBoard = listOfBoards.find(b => b.code() === board)

    if (!matchingRate || !matchingBoard) return

    const criteria = CriteriaAvailability.fromLiterals({
      paymentPolicy: matchingRate.paymentPolicy(),
      cancellationPolicyFromDate: matchingRate.cancellationPolicyFromDate(),
      cancellationPolicyAmount: matchingRate.cancellationPolicyAmount(),
      currency: matchingRate.currency(),
      boardCode: matchingBoard.code(),
    })
    const availability = listOfAvailability.findAvailabilityByCriteria({ criteria })

    if (availability) {
      setAvailabilityMatch(availability)

      const changeKind = availabilityMatch?.boardCode() !== board ? 'board' : 'rate'
      onChange?.({ kind: changeKind, availability })
    }
  }

  // Show placeholder when loading data
  if (loadingMarket || isLoadingAffiliate) return <Placeholder />

  if (!availabilityMatch || errorMarket || !market || errorAffiliate || !affiliate) {
    return null
  }

  const priceType = PriceType.fromAffiliatePriceType({
    affiliate,
    market,
  })

  const currentMatchingAvailabilityValues = {
    rate: `${availabilityMatch.paymentPolicy()}_${availabilityMatch.cancellationPolicyFromDate() ?? ''}_${availabilityMatch.cancellationPolicyAmount() ?? ''}`,
    board: availabilityMatch.boardCode(),
  }

  const getDiff = ({
    paymentPolicy,
    cancellationPolicyFromDate,
    cancellationPolicyAmount,
    currency,
    boardCode,
  }: {
    paymentPolicy: PaymentPolicies
    cancellationPolicyFromDate?: string
    cancellationPolicyAmount?: number
    currency: Currencies
    boardCode: string
  }) => {
    const criteria = CriteriaAvailability.fromLiterals({
      paymentPolicy,
      cancellationPolicyFromDate,
      cancellationPolicyAmount,
      currency,
      boardCode,
    })

    const availability = listOfAvailability.findAvailabilityByCriteria({ criteria })

    if (!availability || !availabilityMatch) return { diff: null }

    const diff = availability.priceDiff(availabilityMatch.price())

    return {
      diff: diff.getDiff(),
    }
  }

  const handleSubmit = () => {
    if (availabilityMatch) {
      onSelectAvailability(availabilityMatch)
    }
  }

  return (
    <Form
      onChange={handleChange}
      defaultValue={currentMatchingAvailabilityValues}
      onSubmit={handleSubmit}
    >
      <main className={styles.main}>
        <RadioSection
          label={i18n.formatMessage({ id: 'filter-category.payment-title' })}
          info={<Info model={availabilityMatch.rate()} />}
          options={listOfRates}
          perNight={priceType.isFeaturedPricePerNight()}
        >
          {rate => {
            const data = getDiff({
              paymentPolicy: rate.paymentPolicy(),
              cancellationPolicyFromDate: rate?.cancellationPolicyFromDate(),
              cancellationPolicyAmount: rate?.cancellationPolicyAmount(),
              currency: availabilityMatch.currency(),
              boardCode: availabilityMatch.boardCode(),
            })

            let label = null

            if (rate.isNRF()) {
              label = (
                <ul>
                  <Typography variant="body" size="l" as="li">
                    {i18n.formatMessage({ id: 'global.pay-now-full-amount' })}
                  </Typography>
                  <Typography variant="body" size="l" bold as="li">
                    {i18n.formatMessage({ id: 'reservation.no-refundable' })}
                  </Typography>
                </ul>
              )
            }

            if (rate.isSFL()) {
              label = (
                <ul>
                  <Typography variant="body" size="l" as="li">
                    {i18n.formatMessage(
                      { id: 'room.pay-partial' },
                      {
                        pay_rate: rate.cancellationPriceFormatted({
                          locale: i18n.locale,
                          market,
                        }),
                      }
                    )}
                    &nbsp;({i18n.formatMessage({ id: 'policies.deposit' })})
                  </Typography>
                  <Typography
                    variant="body"
                    size="l"
                    bold
                    as="li"
                    className={styles.featured as UtilityClassNames}
                  >
                    {i18n.formatMessage(
                      { id: 'policies.pay-the-rest-on' },
                      { date: rate.cancellationDateFormatted({ locale: i18n.locale }) }
                    )}
                  </Typography>
                  <Typography
                    variant="body"
                    size="l"
                    as="p"
                    bold
                    className={styles.featured as UtilityClassNames}
                  >
                    {i18n.formatMessage(
                      { id: 'policies.free-cancellation-before' },
                      { date: rate.cancellationDateFormatted({ locale: i18n.locale }) }
                    )}
                  </Typography>
                </ul>
              )
            }

            if (rate.isNOR()) {
              label = (
                <ul>
                  <Typography variant="body" size="l" as="li">
                    {i18n.formatMessage(
                      { id: 'policies.pay-on' },
                      { date: rate.cancellationDateFormatted({ locale: i18n.locale }) }
                    )}
                  </Typography>

                  <Typography
                    variant="body"
                    size="l"
                    as="p"
                    bold
                    className={styles.featured as UtilityClassNames}
                  >
                    {i18n.formatMessage(
                      { id: 'policies.free-cancellation-before' },
                      { date: rate.cancellationDateFormatted({ locale: i18n.locale }) }
                    )}
                  </Typography>
                </ul>
              )
            }

            if (rate.isNORE()) {
              label = (
                <ul>
                  <Typography variant="body" size="l" as="li">
                    {i18n.formatMessage({ id: 'global.pay-now-full-amount' })}
                  </Typography>
                  <Typography
                    variant="body"
                    size="l"
                    as="li"
                    bold
                    className={styles.featured as UtilityClassNames}
                  >
                    {i18n.formatMessage(
                      { id: 'policies.fully-refundable-until' },
                      { date: rate.cancellationDateFormatted({ locale: i18n.locale }) }
                    )}
                  </Typography>
                </ul>
              )
            }

            const value = `${rate.paymentPolicy()}_${rate.cancellationPolicyFromDate() ?? ''}_${rate.cancellationPolicyAmount() ?? ''}`

            return (
              <RadioItem
                key={value}
                name="rate"
                value={value}
                label={label}
                diff={data.diff}
                disabled={loadingBookingRule}
              />
            )
          }}
        </RadioSection>

        <RadioSection
          label={i18n.formatMessage({ id: 'filter-category.meal-plan' })}
          info={<Info model={availabilityMatch.board()} />}
          options={listOfBoards}
          perNight={priceType.isFeaturedPricePerNight()}
        >
          {board => {
            const boardDescription = board.shortDescription()
            if (!boardDescription) return null

            const data = getDiff({
              paymentPolicy: availabilityMatch.paymentPolicy(),
              cancellationPolicyFromDate: availabilityMatch.cancellationPolicyFromDate(),
              cancellationPolicyAmount: availabilityMatch.cancellationPolicyAmount(),
              currency: availabilityMatch.currency(),
              boardCode: board.code(),
            })

            // eslint-disable-next-line s4l/require-i18n-formatmessage-id-literal
            const label = i18n.formatMessage({ id: boardDescription })

            return (
              <RadioItem
                key={board.code()}
                name="board"
                value={board.code()}
                label={label}
                diff={data.diff}
                disabled={loadingBookingRule}
              />
            )
          }}
        </RadioSection>
      </main>
      <Footer
        availability={availabilityMatch}
        loadingBookingRule={loadingBookingRule}
        disabled={!availabilityMatch || loading || loadingBookingRule}
      />
    </Form>
  )
}

RoomConfiguratorForm.displayName = 'RoomConfigurator.Form'
RoomConfiguratorForm.Placeholder = Placeholder

export default RoomConfiguratorForm
